import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { PromiseWatch } from "./lib/promise-helpers"

export type LineItem = {
  propertyid: number
  lineitemid: number
  parentlineitemid: number | null
  type: 'building_capital' | 'expense' | 'misc_income' | 'hosp_dept_expense' | 'hosp_dept_revenue'
  code: string
  name: string
  systeminput: null | 'system' | 'systemImmutable'
}
// private module logic //

const id: string = "lineItems"
const initialState: ApiResult<LineItem[]> = { id, state: "init", value: [] }
const lineItemsStream = new BehaviorSubject<ApiResult<LineItem[]>>(initialState)
const lineItemsEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let lineItemsMap: {[key: number]: LineItem | null} = {}
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

function sortLineItems(data: LineItem[]): LineItem[] {
  data.sort((a, b) => {
    const atype = _.get(a, "type")
    const btype = _.get(b, "type")
    if(atype === btype) return 0
    else if(atype === "misc_income" && btype !== "misc_income") return -1
    else if(atype === "expense" && btype !== "misc_income") return -1
    else if(atype === "expense_non_recov" && btype !== "misc_income" && btype !== "expense") return -1
    else return 1
  })

  return data
}

function sortDepartmentalLineItems(data: LineItem[]): LineItem[] {
  return _.orderBy(data, [(o) => o.systeminput !== null, "name", "type"], ["desc", "asc", "desc"])
}

function findKinRecord(lineItem, lineItems): LineItem | null {
  if(lineItem.parentlineitemid) {
    return _.find(lineItems, r => r.lineitemid === lineItem.parentlineitemid)
  } else {
    return _.find(lineItems, r => r.parentlineitemid === lineItem.lineitemid)
  }
}

function clear(): void {
  lineItemsStream.next({ id, state: 'ok', value: [] })
  lineItemsMap = {}
}

function get(lineitemid: number): LineItem | null {
  return lineItemsMap[lineitemid]
}

function refresh(): Promise<any> {
  const currentValue = lineItemsStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchLineItems(propertyid)
}

function fetchLineItems(
  propertyId: number,
  limit: number = 0,
  offset: number = 0,
  noclear = false
): Promise<LineItem[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  noclear || clear()
  lineItemsStream.next({ id, state: 'busy', value: lineItemsStream.getValue().value })

  let url = `lineitem?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `lineitem?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: LineItem[]) => {
    _.forEach(records, (lineItems) => (lineItemsMap[lineItems.lineitemid] = lineItems))
    // merge results unique by lineitemid
    let val = _.concat(
      sortLineItems(_.filter(records, li => li.type !== "hosp_dept_revenue" && li.type !== "hosp_dept_expense")),
      sortDepartmentalLineItems(_.filter(records, li => li.type === "hosp_dept_revenue" || li.type === "hosp_dept_expense"))
    )
    lineItemsStream.next({
      id,
      state: 'ok',
      value: val
    })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function fetchLineItemsByType(
  propertyId: number,
  type: string,
  limit: number = 0,
  offset: number = 0
): Promise<LineItem[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  lineItemsStream.next({ id, state: 'busy', value: lineItemsStream.getValue().value })

  let url = `lineitem?propertyid=eq.${propertyId}&type=eq.${type}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `lineitem?propertyid=eq.${propertyId}&type=eq.${type}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: LineItem[]) => {
    _.forEach(records, (lineItems) => (lineItemsMap[lineItems.lineitemid] = lineItems))
    // merge results unique by lineitemid
    lineItemsStream.next({ id, state: 'ok', value: sortLineItems(records) })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

type LIDefaultsPartial = { type?: "building_capital" | "expense_non_recov" | "expense" | "misc_income"}
function create(propertyid: number, defaults: LIDefaultsPartial, type: string, parentId?: number): Promise<LineItem | { message: string }> {
  const currentValue = lineItemsStream.getValue().value
  lineItemsStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    amttype: type === "DEPARTMENTAL_EXPENSE" ? null : defaults['amttype'],
    inflationdetail: _.includes(type, "DEPARTMENTAL") ? null : {
      inflationNamedItemId: _.get(defaults, ['inflationdetail', `type_${defaults.type}`, 'inflationNamedItemId'])
    },
    inputtype: type === "DEPARTMENTAL_EXPENSE" ? "percentLineItem" : "amt",
    lineitemdetail: type === "DEPARTMENTAL_EXPENSE" ? {
      state: "percentLineItem",
      items: [{system: false, lineItemId: parentId, percent: 0}]
    }
    :
    {
      state: 'basic'
    },
    parentlineitemid: type === "DEPARTMENTAL_EXPENSE" ? parentId : null,
    propertyid,
    reportstatus: true,
    type: defaults.type ? defaults.type :
          type === "HOSPITALITY" ? "hosp_undist_expense" :
          type === "DEPARTMENTAL" ? "hosp_dept_revenue" :
          type === "DEPARTMENTAL_EXPENSE" ? "hosp_dept_expense" :
          "expense"
  }

  return Client.post(`lineitem`, payload)
    .then((lineItems: LineItem) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, lineItems.lineitemid),
        redofunc: () => create(propertyid, defaults, type)
      })
      lineItemsMap[lineItems.lineitemid] = lineItems
      // merge results unique by lineitemid
      const currentValue = lineItemsStream.getValue().value
      lineItemsStream.next({
        id,
        state: 'ok',
        value: _.unionBy([lineItems], currentValue, 'lineitemid')
      })
      lineItemsEventStream.next({ type: 'LINEITEM_CREATED', payload: lineItems })

      // create child departmental expense
      if(type === "DEPARTMENTAL") {
        create(propertyid, defaults, "DEPARTMENTAL_EXPENSE", _.get(lineItems, "lineitemid"))
      }
      // sort in-place newly created departmental line items
      if(type === "DEPARTMENTAL_EXPENSE") {
        const curr = lineItemsStream.getValue().value
        let revIdx = _.findIndex(curr, i => i.lineitemid === parentId)
        let expIdx = _.findIndex(curr, i => i.lineitemid === _.get(lineItems, "lineitemid"))
        let tmp = curr[revIdx]
        curr[revIdx] = curr[expIdx]
        curr[expIdx] = tmp
        lineItemsStream.next({
          id,
          state: 'ok',
          value: curr
        })
      }
      return lineItems
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      lineItemsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchLineItems(propertyid)
      return err
    })
}

function copy(propertyid:number, lineitemid:number, code:string) {
  const currentValue = lineItemsStream.getValue().value;
  lineItemsStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'lineitem', recordid: lineitemid, _synchronous: true, unique: true} //code: incrementUnique(code, "code", currentValue)};

  // only copy parent departmental line items - copy_record helper takes care of the children
  const target = lineItemsMap[lineitemid]
  if (!target) return
  if (target.type == 'hosp_dept_expense') {
    if (!target.parentlineitemid) return
    const parent = lineItemsMap[target.parentlineitemid]
    if (!parent) return
    payload.recordid = parent.lineitemid
  }
  if (target.type == 'hosp_dept_expense' || target.type == 'hosp_dept_revenue') {
    _.set(payload, "name", target.name || '-')
    _.set(payload, "patronym", true)
  }
  // const target = lineItemsMap[lineitemid]
  // if(target?.type === "hosp_dept_expense" || target?.type === "hosp_dept_revenue") {
  //   let record: LineItem | null = target
  //   if(record?.type === "hosp_dept_expense") record = findKinRecord(target, currentValue)
  //   payload.recordid = record?.lineitemid || 0
  //   _.set(payload, "code", record?.code ? `${record.code}` : "Empty")
  //   _.set(payload, "name", record?.name ? `${record.name}` : "Empty")
  //   _.set(payload, "patronym", true)
  //   _.set(payload, "unique", true)
  // }

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((lineItems: LineItem[]) => {
      _.each(lineItems, (newRecord) => {
        push_undo({
          undofunc: () => deleteOne(propertyid, newRecord.lineitemid),
          redofunc: () => copy(propertyid, lineitemid, code)
        });
        lineItemsMap[newRecord.lineitemid] = newRecord
        lineItemsEventStream.next({ type: 'LINEITEM_COPIED', payload: newRecord })
      })
      return fetchLineItems(propertyid,0,0,true)
      // merge results unique by lineitemid
      const currentValue = lineItemsStream.getValue().value
      lineItemsStream.next({
        id,
        state: 'ok',
        value: _.unionBy(lineItems, currentValue, 'lineitemid')
      })
      // NB moved to svc_recordcopy Bastille Day '23
      // // patch %ofLine for departmental expense copies so that they point at the correct parent if such a ref exists (not the original copy)
      // if(target?.type === "hosp_dept_expense" || target?.type === "hosp_dept_revenue") {
      //   let expense = _.cloneDeep(_.find(lineItems, lineItem => lineItem.type === "hosp_dept_expense"))
      //   let revenue = _.find(lineItems, lineItem => lineItem.type === "hosp_dept_revenue")
      //   let originalParentItem = _.chain(expense).get("lineitemdetail.items").find(p => p.lineItemId === target.parentlineitemid).value()
      //   if(_.get(expense, "lineitemdetail.state") === "percentLineItem" && originalParentItem) {
      //     expense.lineitemdetail = {
      //       state: "percentLineItem",
      //       items: [{system: false, lineItemId: revenue.lineitemid, percent: originalParentItem.percent}]
      //     }
      //     patch(expense)
      //   }
      // }
      return lineItems
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      lineItemsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchLineItems(propertyid)
      return err
    })
}

function copies(propertyid:number, lineitemid:number, count: number) {
  const currentValue = lineItemsStream.getValue().value;
  lineItemsStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'lineitem', recordid: lineitemid, _synchronous: true, unique: true, count};

  // only copy parent departmental line items - copy_record helper takes care of the children
  const target = lineItemsMap[lineitemid]
  if (!target) return
  if (target.type == 'hosp_dept_expense') {
    if (!target.parentlineitemid) return
    const parent = lineItemsMap[target.parentlineitemid]
    if (!parent) return
    payload.recordid = parent.lineitemid
  }
  if (target.type == 'hosp_dept_expense' || target.type == 'hosp_dept_revenue') {
    _.set(payload, "name", target.name || '-')
    _.set(payload, "patronym", true)
  }

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((lineitems: LineItem[]) => {
      push_undo({
        undofunc: () => deleteMultiple(propertyid, _.map(lineitems, 'lineitemid')),
        redofunc: () => copies(propertyid, lineitemid, _.map(lineitems, 'lineitemid'))
      });
      _.forEach(lineitems, newRecord => lineItemsMap[newRecord.lineitemid] = newRecord)
      lineItemsEventStream.next({ type: 'LINEITEM_COPIED', payload: lineitems })
      return fetchLineItems(propertyid,0,0,true)
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      lineItemsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchLineItems(propertyid)
      return err
    })
}

async function deleteOne(
  propertyid: number,
  lineitemid: number
): Promise<boolean | { message: string }> {
  const currentValue = lineItemsStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, lineitemid)
  const refreshFn = () => fetchLineItems(propertyid)
  if (!(await handleReferences('lineitem', lineitemid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted lineitemid
  const value = _(currentValue)
    .filter((p) => p.lineitemid !== lineitemid)
    .value()
  value.forEach((p) => (lineItemsMap[p.lineitemid] = p))

  lineItemsStream.next({ id, state: 'ok', value })

  // delete the corresponding expense/revenue for departmental types
  const target = lineItemsMap[lineitemid]
  if(target?.type === "hosp_dept_expense" || target?.type === "hosp_dept_revenue") {
    const kinRecord = findKinRecord(target, currentValue)
    if(kinRecord) {
      // always delete the expense record first or else it will violate FK constraint
      if(kinRecord.type === "hosp_dept_expense") {
        await deleteOne(kinRecord.propertyid, kinRecord.lineitemid)
      } else {
        setTimeout(() => deleteOne(kinRecord.propertyid, kinRecord.lineitemid), 0)
      }
    }
  }

  // update the db
  return _patches_.all(lineitemid).then(() =>
  Client.del(`lineitem?lineitemid=eq.${lineitemid}`)
    .then((lineItems: LineItem) => {
      delete lineItemsMap[lineItems.lineitemid]
      push_undo({
        undofunc: () => {
          Client.post('lineItems', lineItems)
          lineItemsMap[lineItems.lineitemid] = lineItems
          lineItemsStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, lineitemid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      lineItemsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchLineItems(propertyid)
      return err
    })
  )
}

async function deleteMultiple(
  propertyid: number,
  lineitemids: number[]
): Promise<boolean | { message: string }> {
  const currentValue = lineItemsStream.getValue().value

  const deleteFn = (ids) => deleteMultiple(propertyid, ids)
  const refreshFn = () => fetchLineItems(propertyid)
  if (!(await handleReferences('lineitem', lineitemids, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted lineitemid
  const value = _(currentValue)
    .filter((p) => !lineitemids.includes(p.lineitemid))
    .value()
  value.forEach((p) => (lineItemsMap[p.lineitemid] = p))

  lineItemsStream.next({ id, state: 'ok', value })

  // delete the corresponding expense/revenue for departmental types
  await Promise.all(_.map(lineitemids, async (lineitemid) => {
    const target = lineItemsMap[lineitemid]
    if(target?.type === "hosp_dept_expense" || target?.type === "hosp_dept_revenue") {
      const kinRecord = findKinRecord(target, currentValue)
      if(kinRecord) {
        // always delete the expense record first or else it will violate FK constraint
        if(kinRecord.type === "hosp_dept_expense") {
          await deleteOne(kinRecord.propertyid, kinRecord.lineitemid)
        } else {
          setTimeout(() => deleteOne(kinRecord.propertyid, kinRecord.lineitemid), 0)
        }
      }
    }
  }))

  // update the db
  return _patches_.all(lineitemids).then(() =>
  Client.del(`lineitem?lineitemid=in.${lineitemids}`)
    .then((lineItems: LineItem[]) => {
      _.each(lineItems, l => delete lineItemsMap[l.lineitemid])
      push_undo({
        undofunc: () => {
          Client.post('lineItems', lineItems)
          _.each(lineItems, l => lineItemsMap[l.lineitemid] = l)
          lineItemsStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteMultiple(propertyid, lineitemids)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      lineItemsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchLineItems(propertyid)
      return err
    })
  )
}

function patch(lineItem: LineItem): Promise<LineItem | { message: string }> {
  const currentValue = lineItemsStream.getValue().value
  const lineitemid = lineItem.lineitemid
  const currentlineItems = _.find(currentValue, { lineitemid })
  const changeset = diff_obj(lineItem, currentlineItems)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentlineItems),
    redofunc: () => patch(lineItem)
  })

  // optimistic update
  lineItemsStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {lineitemid}), lineItem)
  })

  return _patches_.watch(lineitemid, Client.patch(`lineitem?lineitemid=eq.${lineitemid}`, changeset)
    .then((lineitems: LineItem[]) => {
      _.forEach(lineitems, (lineItems) => (lineItemsMap[lineItems.lineitemid] = lineItems))
      return lineitems[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      lineItemsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentlineItems, 'propertyid')
      if (propertyid) fetchLineItems(propertyid)
      return err
    }))

}

function clearDetailAccounts(propertyid: number) {
  Client.patch(`lineitem?propertyid=eq.${propertyid}`, {detailaccountid: null})
    .then((lineitems: LineItem[]) => {
      _.forEach(lineitems, (li) => (lineItemsMap[li.lineitemid] = li))
      return lineitems
    })
    .catch((err: { message: string }) => {
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      return err
    })
}

// public module interface //

const lineItemsService = {
  stream: () => lineItemsStream.asObservable(),
  value: () => lineItemsStream.getValue().value,
  eventStream: () => lineItemsEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  deleteMultiple,
  get,
  fetchLineItems,
  fetchLineItemsByType,
  patch,
  refresh,
  clearDetailAccounts
}

export default lineItemsService
